import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { B } from '../constants';
import EmptyState from './ui/EmptyState';
import LoadingState from './ui/LoadingState';
import { ImgOrFallback } from './FallbackImg';
import L from 'leaflet';

// Keyless OpenStreetMap tiles by default (CARTO basemaps now require a key).
// Override with any XYZ provider, e.g. a self-hosted tile server.
const TILE_URL = process.env.REACT_APP_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = process.env.REACT_APP_MAP_TILE_ATTRIBUTION
  || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const isMobile = typeof window !== 'undefined' && L.Browser && L.Browser.mobile;

// Compact (inline) maps show only the route: muted base tiles so the stops and the
// line stand out, no zoom buttons or walk labels. Expanding adds those details.
const COMPACT_CSS = '.gb-map--compact .leaflet-tile-pane{filter:saturate(.3) brightness(1.06) contrast(.9)}';

// Expanded maps grow in place, like Google's inline map card, instead of covering the page.
function expandedPx(height) {
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const target = Math.round(vh * (isMobile ? 0.72 : 0.78));
  return Math.max(typeof height === 'number' ? height : 0, Math.min(target, 720));
}

// Keeps Leaflet sized and interactive to match the inline/expanded frame. Inline on a
// phone the map stays still so the page scrolls past it; expanded, it pans and pinches.
function FrameSync({ expanded, markers }) {
  const map = useMap();
  useEffect(() => {
    const interactive = expanded || !isMobile;
    [map.dragging, map.touchZoom].forEach((h) => h && (interactive ? h.enable() : h.disable()));
    if (map.scrollWheelZoom) (expanded || !isMobile ? map.scrollWheelZoom.enable() : map.scrollWheelZoom.disable());
    // A popup sized for the old frame would be clipped after the resize.
    if (map.closePopup) map.closePopup();
    const t = setTimeout(() => {
      map.invalidateSize();
      if (markers.length > 1) map.fitBounds(L.latLngBounds(markers.map((m) => m.position)), { padding: [40, 40] });
    }, 60);
    return () => clearTimeout(t);
  }, [expanded, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

const frameBtn = {
  position: 'absolute', top: 10, right: 10, zIndex: 1000, width: 36, height: 36, borderRadius: 10,
  border: 'none', background: '#fff', color: '#1f2a24', boxShadow: '0 1px 6px rgba(0,0,0,.3)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
};

const ExpandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);
const CollapseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
  </svg>
);

const chipStyle = {
  position: 'absolute', top: 10, left: 10, zIndex: 1000, padding: '5px 10px', borderRadius: 999,
  background: 'rgba(31,42,36,.88)', color: '#fff', fontSize: 12, fontWeight: 700,
  fontFamily: "'Montserrat', sans-serif", pointerEvents: 'none', whiteSpace: 'nowrap',
};

// Haversine distance in meters
function haversineDistance([lat1, lng1], [lat2, lng2]) {
  const R = 6371000; // meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

// Fix for default marker icons in Leaflet
if (L.Icon && L.Icon.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
}

// Default icon fallback
const defaultIcon = L.divIcon({
  className: 'custom-default-marker',
  html: `<div style="background:#1a6b4a;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)">●</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

function FitBounds({ markers }) {
  const map = useMap();
  // Refit only when the set of positions changes, not on every re-render (e.g. selecting a
  // pin), so a user's zoom isn't reset each time they tap a marker.
  const positionsKey = markers.map((m) => m.position.join(',')).join('|');
  useEffect(() => {
    if (markers.length > 1) {
      const bounds = L.latLngBounds(markers.map(m => m.position));
      map.fitBounds(bounds, { padding: [40, 40] });
    } else if (markers.length === 1) {
      map.flyTo(markers[0].position, 12);
    }
  }, [positionsKey, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Does this marker represent the given activity? Prefer the stable
// (dayNumber, activityIndex) pair; fall back to title equality for callers that
// don't supply them.
function matchesActivity(marker, activity) {
  if (!activity || !marker) return false;
  const hasIds = activity.activityIndex !== undefined &&
    activity.dayNumber !== undefined &&
    marker.activityIndex !== undefined &&
    marker.dayNumber !== undefined;
  if (hasIds) {
    return activity.dayNumber === marker.dayNumber &&
      activity.activityIndex === marker.activityIndex;
  }
  return activity.activity === marker.title || activity.title === marker.title;
}

export default function MapView({ locations = [], center, hoveredActivity, selectedId: selectedLocId, onActivityHover, height = 400, showRoute = true, expandable }) {
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const frameRef = useRef(null);
  // A map sized by its parent (height '100%') is already as big as its slot, so it
  // shows full detail and has no expand button.
  const canExpand = expandable ?? !(typeof height === 'string' && height.trim().endsWith('%'));
  const detailed = expanded || !canExpand;
  // Leaflet marker instances by marker id, so selecting an item in the list can
  // open that marker's description popup (not just recolour the pin).
  const markerRefs = useRef({});

  // Default center (first location or default)
  const defaultCenter = center || [20, 0];
  
  // Process locations into markers
  const markers = locations
    .filter(loc => loc.lat && loc.lng)
    .map((loc, idx) => ({
      id: loc.id || idx,
      position: [loc.lat, loc.lng],
      title: loc.name || loc.title || 'Location',
      description: loc.description,
      photoUrl: loc.photoUrl || '',
      imageUrl: loc.imageUrl || loc.photoUrl || '',
      actions: Array.isArray(loc.actions) ? loc.actions.filter(Boolean) : [],
      source: loc,
      dayNumber: loc.dayNumber,
      pin: !!loc.pin,
      activityIndex: loc.activityIndex,
      label: loc.label,
    }));

  useEffect(() => {
    setLoading(false);
  }, []);

  // Expanded: bring the whole (taller) map into view and collapse on Escape.
  useEffect(() => {
    if (!expanded) return undefined;
    const t = setTimeout(() => frameRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }), 80);
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  // Open the popup of the marker matching the selected/hovered activity and close
  // the previously opened one, so the map description follows the itinerary list
  // selection instead of only responding to direct marker clicks.
  const isSelected = (m) => (selectedLocId != null ? String(m.id) === String(selectedLocId) : matchesActivity(m, hoveredActivity));
  const selectedMarker = markers.find(isSelected);
  const selectedId = selectedMarker ? String(selectedMarker.id) : null;
  const openedIdRef = useRef(null);
  useEffect(() => {
    const previous = markerRefs.current[openedIdRef.current];
    if (openedIdRef.current !== selectedId && previous?.closePopup) previous.closePopup();
    openedIdRef.current = selectedId;
    if (selectedId == null) return;
    markerRefs.current[selectedId]?.openPopup();
  }, [selectedId]);

  if (loading) {
    return (
      <div style={{
        height,
        background: B.sand,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
      }}>
        <LoadingState variant="spinner" message="Loading map..." />
      </div>
    );
  }

  if (markers.length === 0) {
    return (
      <div style={{
        height,
        background: B.sand,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
      }}>
        <EmptyState
          icon="🗺️"
          title="No locations to display"
          description="Add destinations to your trip to see them on the map."
          variant="minimal"
        />
      </div>
    );
  }

  // Create custom marker icon — photo thumbnail when available, numbered fallback
  const routeMarkers = showRoute ? markers.filter((m) => !m.pin) : [];

  const routeKm = routeMarkers.slice(1).reduce((sum, m, i) => sum + haversineDistance(routeMarkers[i].position, m.position), 0) / 1000;
  const routeSummary = routeMarkers.length > 1
    ? `${routeMarkers.length} stops · ~${routeKm < 10 ? routeKm.toFixed(1) : Math.round(routeKm)} km`
    : null;

  const markerLabel = (marker) => (marker.label != null ? String(marker.label) : `D${marker.dayNumber || ''}`);

  const createMarkerIcon = (marker, isHovered = false, withPhoto = true) => {
    // Small dot for browse pins (places not yet in a plan), so many of them stay readable.
    if (marker.pin) {
      const s = isHovered ? 18 : 12;
      return L.divIcon({
        className: 'custom-pin-marker',
        html: `<div style="width:${s}px;height:${s}px;border-radius:50%;background:${isHovered ? '#4ade80' : '#e8a33d'};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.35)"></div>`,
        iconSize: [s, s],
        iconAnchor: [s / 2, s / 2],
      });
    }
    const size = isHovered ? 44 : 36;
    const border = isHovered ? '3px solid #4ade80' : '2px solid white';
    const shadow = isHovered ? '0 3px 12px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.3)';
    if (withPhoto && marker.photoUrl) {
      return L.divIcon({
        className: 'custom-photo-marker',
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;overflow:hidden;border:${border};box-shadow:${shadow};background:#fff;display:flex;align-items:center;justify-content:center"><img src="${marker.photoUrl}" style="width:100%;height:100%;object-fit:cover" onerror="this.style.display='none';this.parentElement.innerHTML='${markerLabel(marker)}'"/></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    }
    return L.divIcon({
      className: 'custom-day-marker',
      html: `<div style="background:${isHovered ? '#4ade80' : '#1a6b4a'};color:white;width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:${isHovered ? '14px' : '12px'};border:${border};box-shadow:${shadow}">${markerLabel(marker)}</div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  return (
    // `isolation` keeps Leaflet's internal z-indexes (panes 400, controls 1000) inside
    // this box, so the map scrolls under the sticky navbar and bottom tab bar instead
    // of painting over them.
    <div
      ref={frameRef}
      data-testid="map-frame"
      className={`gb-map ${detailed ? 'gb-map--detailed' : 'gb-map--compact'}`}
      style={{
        position: 'relative', zIndex: 0, isolation: 'isolate', borderRadius: 16, overflow: 'hidden',
        height: expanded ? expandedPx(height) : height,
        touchAction: expanded ? 'none' : (isMobile ? 'pan-x pan-y' : 'auto'),
      }}
    >
      <style>{COMPACT_CSS}</style>
      {routeSummary && <div style={chipStyle}>{routeSummary}</div>}
      {canExpand && (
        <button type="button" onClick={() => setExpanded((v) => !v)} style={frameBtn} aria-expanded={expanded}
          aria-label={expanded ? 'Collapse map' : 'Expand map'} title={expanded ? 'Collapse map' : 'Expand map'}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </button>
      )}
      <MapContainer
        center={defaultCenter}
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={!isMobile}
        dragging={!isMobile}
        tap={false}
        touchZoom={false}
        zoomControl={false}
      >
        <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} maxZoom={19} />
        {detailed && <ZoomControl position="bottomright" />}
        <FitBounds markers={markers} />
        <FrameSync expanded={expanded} markers={markers} />
        
        {markers.map((marker) => {
          const isHovered = isSelected(marker);
          return (
            <Marker
              key={marker.id}
              ref={(instance) => {
                if (instance) markerRefs.current[marker.id] = instance;
                else delete markerRefs.current[marker.id];
              }}
              position={marker.position}
              icon={marker.dayNumber || marker.pin || marker.label != null ? createMarkerIcon(marker, isHovered, detailed) : defaultIcon}
              eventHandlers={{
                click: () => onActivityHover?.(marker),
              }}
            >
              <Popup>
                <div style={{ minWidth: 150, maxWidth: 220 }}>
                  {detailed && marker.imageUrl && (
                    <ImgOrFallback src={marker.imageUrl} alt="" loading="lazy"
                      style={{ display: 'block', width: '100%', height: 96, objectFit: 'cover', borderRadius: 8, marginBottom: 6 }} />
                  )}
                  {marker.dayNumber && (
                    <p style={{ 
                      fontSize: 10, 
                      color: B.forest, 
                      fontWeight: 700,
                      marginBottom: 4,
                      textTransform: 'uppercase',
                    }}>
                      Day {marker.dayNumber}
                    </p>
                  )}
                  <h4 style={{ 
                    fontFamily: "'Playfair Display', serif", 
                    fontSize: 14, 
                    marginBottom: 4,
                  }}>
                    {marker.title}
                  </h4>
                  {detailed && marker.description && (
                    <p style={{ 
                      fontSize: 11, 
                      color: '#666',
                      fontFamily: "'Montserrat', sans-serif",
                    }}>
                      {marker.description.slice(0, 100)}
                      {marker.description.length > 100 ? '...' : ''}
                    </p>
                  )}
                  {marker.actions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {marker.actions.map((a) => (
                        <button key={a.label} type="button" disabled={!!a.disabled} aria-label={a.ariaLabel || a.label}
                          onClick={(e) => { e.stopPropagation(); a.onClick && a.onClick(); }}
                          style={{
                            padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat', sans-serif",
                            border: `1px solid ${a.disabled ? '#e5e5e5' : B.forest}`, background: a.primary && !a.disabled ? B.forest : '#fff',
                            color: a.disabled ? '#999' : (a.primary ? '#fff' : B.forest), cursor: a.disabled ? 'default' : 'pointer',
                          }}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Route line between consecutive stops (browse pins are not a route) */}
        {routeMarkers.length > 1 && (
          <>
            <Polyline positions={routeMarkers.map(m => m.position)} pathOptions={{ color: '#fff', weight: 7, opacity: 0.9 }} />
            <Polyline positions={routeMarkers.map(m => m.position)} pathOptions={{ color: '#1a6b4a', weight: 4, opacity: 0.95 }} />
          </>
        )}

        {/* Walking time labels between consecutive markers (expanded detail) */}
        {detailed && routeMarkers.length > 1 && routeMarkers.slice(0, -1).map((marker, idx) => {
          const next = routeMarkers[idx + 1];
          const dist = haversineDistance(marker.position, next.position);
          const walkMins = Math.round(dist / 80); // ~80m/min walking
          if (walkMins < 1 || walkMins > 60) return null;
          const midpoint = [(marker.position[0] + next.position[0]) / 2, (marker.position[1] + next.position[1]) / 2];
          return (
            <Marker
              key={`walk-${idx}`}
              position={midpoint}
              icon={L.divIcon({
                className: 'walk-label',
                html: `<div style="background:white;padding:2px 6px;border-radius:10px;font-size:10px;font-weight:600;box-shadow:0 1px 4px rgba(0,0,0,0.2)">🚶 ${walkMins}min</div>`,
                iconSize: [60, 20],
                iconAnchor: [30, 10],
              })}
            />
          );
        })}
      </MapContainer>
    </div>
  );
}
